import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BaseApiService } from './api-base.service';
import { HelperService } from './helper.service';
import {
  WorklogApiResponse,
  MaxQueryDateResponse,
  ActivityQueryResponse,
  DeleteActivityResponse,
  ClientsApiResponse
} from '../models/worklog.model';

/**
 * Service for Worklog API integration
 * See docs/worklog-editor/03-api-integration.md
 */
@Injectable({
  providedIn: 'root'
})
export class WorklogService extends BaseApiService {
  private env: string;

  constructor(
    http: HttpClient,
    msalService: MsalService,
    private helperService: HelperService
  ) {
    super(http, msalService);
    this.env = this.helperService.getStoredValue('ecase_env', 'prod');
  }

  /**
   * Get worklog for a specific date
   * @param date Date to query
   * @returns Observable of worklog response
   */
  getWorklogForDate(date: Date): Observable<WorklogApiResponse> {
    const formattedDate = this.formatDateForApi(date);
    const url = `${this.apiUrl}/worklog/${this.env}/residents?from_date=${formattedDate}&to_date=${formattedDate}`;

    return this.get<WorklogApiResponse>(url).pipe(
      catchError(error => {
        this.helperService.openSnackBar('Failed to load worklog data', 'Close');
        throw error;
      })
    );
  }

  /**
   * Get maximum query date for a client (60 days ahead)
   * @param clientCode Client code
   * @returns Observable of max query date response
   */
  getMaxQueryDate(clientCode: string): Observable<MaxQueryDateResponse> {
    const url = `${this.apiUrl}/worklog/${this.env}/clients/${clientCode}/max-query-date`;

    return this.get<MaxQueryDateResponse>(url).pipe(
      catchError(error => {
        this.helperService.openSnackBar('Failed to get query date range', 'Close');
        throw error;
      })
    );
  }

  /**
   * Query all instances of activities for a client
   * @param clientCode Client code
   * @param fromDate Start date (DD Mon YYYY format)
   * @param toDate End date (DD Mon YYYY format)
   * @returns Observable of activity query response
   */
  queryActivityInstances(
    clientCode: string,
    fromDate: string,
    toDate: string
  ): Observable<ActivityQueryResponse> {
    const url = `${this.apiUrl}/worklog/${this.env}/clients/${clientCode}/query`;
    const body = {
      from_date: fromDate,
      to_date: toDate
    };

    return this.post<ActivityQueryResponse>(url, body).pipe(
      catchError(error => {
        this.helperService.openSnackBar('Failed to query activity instances', 'Close');
        throw error;
      })
    );
  }

  /**
   * Delete an activity (make non-recurring)
   * @param activityId Activity ID to delete
   * @returns Observable of delete response
   */
  deleteActivity(activityId: string): Observable<DeleteActivityResponse> {
    const url = `${this.apiUrl}/worklog/${this.env}/activities/${activityId}/make-non-recurring`;

    return this.post<DeleteActivityResponse>(url, {}).pipe(
      catchError(error => {
        // Don't show snackbar for individual failures during bulk delete
        throw error;
      })
    );
  }

  /**
   * Format a Date object for the API (DD Mon YYYY)
   * @param date Date to format
   * @returns Formatted date string
   */
  formatDateForApi(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  }

  /**
   * Update environment setting
   * @param env New environment ('prod' or 'uat')
   */
  setEnvironment(env: 'prod' | 'uat'): void {
    this.env = env;
  }

  /**
   * Get current environment
   * @returns Current environment
   */
  getEnvironment(): string {
    return this.env;
  }

  /**
   * Get list of all clients/residents with ResidentID and Customer_Code mapping
   * @returns Observable of clients response
   */
  getClients(): Observable<ClientsApiResponse> {
    const url = `${this.apiUrl}/ecase/${this.env}/clients/`;

    return this.get<ClientsApiResponse>(url).pipe(
      catchError(error => {
        this.helperService.openSnackBar('Failed to load client list', 'Close');
        throw error;
      })
    );
  }

  /**
   * Deactivate a chart to prevent future activity generation
   * @param chartId Chart ID
   * @param customerCode Customer Code
   * @param chartTypeId Chart Type ID
   * @returns Observable of deactivation response
   */
  deactivateChart(chartId: string, customerCode: string, chartTypeId: string): Observable<any> {
    const url = `${this.apiUrl}/worklog/${this.env}/charts/${chartId}/deactivate`;
    const body = {
      chart_id: chartId,
      customer_code: customerCode,
      chart_type_id: chartTypeId
    };

    return this.post<any>(url, body).pipe(
      catchError(error => {
        this.helperService.openSnackBar('Failed to deactivate chart', 'Close');
        throw error;
      })
    );
  }
}
